import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Send, Loader2, MessageCircle, X, Phone, Calendar, Star } from 'lucide-react';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Common dial codes for the capture form. `iso` is sent to the backend as the
// parse region; the backend never double-prefixes (it honours an existing +cc).
const DIAL_CODES = [
  { iso: 'US', dial: '+1', flag: '🇺🇸' },
  { iso: 'IN', dial: '+91', flag: '🇮🇳' },
  { iso: 'GB', dial: '+44', flag: '🇬🇧' },
  { iso: 'CA', dial: '+1', flag: '🇨🇦' },
  { iso: 'AU', dial: '+61', flag: '🇦🇺' },
  { iso: 'AE', dial: '+971', flag: '🇦🇪' },
  { iso: 'SG', dial: '+65', flag: '🇸🇬' },
  { iso: 'DE', dial: '+49', flag: '🇩🇪' },
  { iso: 'FR', dial: '+33', flag: '🇫🇷' },
  { iso: 'BR', dial: '+55', flag: '🇧🇷' },
  { iso: 'JP', dial: '+81', flag: '🇯🇵' },
  { iso: 'ZA', dial: '+27', flag: '🇿🇦' },
];

export default function ChatWidget() {
  const { token } = useParams();
  const [config, setConfig] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [messages, setMessages] = useState([{ role: 'assistant', content: 'Hi 👋 How can we help you today?' }]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(`lk_chat_${token}`) || null);
  const lastSeqRef = useRef(0);
  const scrollRef = useRef(null);
  const [cards, setCards] = useState([]);
  const [capForm, setCapForm] = useState({ name: '', email: '', phone: '', country: '' });
  const [consentAsk, setConsentAsk] = useState(false);
  const [slotMt, setSlotMt] = useState(null);
  const [slotDay, setSlotDay] = useState(0);

  const color = config?.primary_color || '#7c3aed';

  const closeWidget = () => { try { window.parent.postMessage('lockene:close', '*'); } catch { /* not embedded */ } };

  const submitCapture = useCallback(async () => {
    const { name, email, phone, country } = capForm;
    if (!email && !phone) return;
    try {
      const res = await axios.post(`${API_BASE}/api/inbound/chat/widget/${token}/capture`, { session_id: sessionId, name, email, phone, country });
      if (res.data?.ok) {
        // Advance the poll cursor so the persisted capture reply isn't re-delivered
        // as a duplicate bubble by the 5s poll loop.
        if (res.data.reply_seq) lastSeqRef.current = Math.max(lastSeqRef.current, res.data.reply_seq);
        if (res.data.reply) setMessages((m) => [...m, { role: 'assistant', content: res.data.reply }]);
        setCards((cs) => cs.filter((c) => c.type !== 'capture_form'));
      }
    } catch { /* ignore */ }
  }, [capForm, sessionId, token]);

  const doBookCall = useCallback(async () => {
    setCards((cs) => cs.filter((c) => c.type !== 'actions'));
    setSlotMt(null); setSlotDay(0);
    try {
      const res = await axios.get(`${API_BASE}/api/inbound/chat/widget/${token}/slots`);
      const days = res.data?.days || [];
      if (days.length && (res.data?.meeting_types || []).length) {
        setCards((cs) => [...cs, { type: 'slot_picker', meeting_type: res.data.meeting_type, meeting_types: res.data.meeting_types, days }]);
        return;
      }
    } catch { /* fall through to text handoff */ }
    // Fallback: no calendar / no open slots → original text handoff
    setMessages((m) => [...m, { role: 'user', content: 'I’d like to book a 15-minute call' }]);
    setSending(true);
    try {
      const r2 = await axios.post(`${API_BASE}/api/inbound/chat/widget/${token}/message`, { session_id: sessionId, message: 'I would like to book a 15-minute call with a specialist' });
      if (r2.data?.reply) setMessages((m) => [...m, { role: 'assistant', content: r2.data.reply }]);
      if (r2.data?.reply_seq) lastSeqRef.current = Math.max(lastSeqRef.current, r2.data.reply_seq);
      if (r2.data?.cards) setCards((cs) => [...cs, ...r2.data.cards]);
    } catch { /* ignore */ } finally { setSending(false); }
  }, [sessionId, token]);

  const changeMt = useCallback(async (mtId) => {
    setSlotMt(mtId); setSlotDay(0);
    try {
      const res = await axios.get(`${API_BASE}/api/inbound/chat/widget/${token}/slots?meeting_type=${mtId}`);
      setCards((cs) => cs.map((c) => c.type === 'slot_picker'
        ? { type: 'slot_picker', meeting_type: res.data.meeting_type, meeting_types: res.data.meeting_types, days: res.data.days || [] } : c));
    } catch { /* ignore */ }
  }, [token]);

  const bookSlot = useCallback(async (mtId, start) => {
    setCards((cs) => cs.filter((c) => c.type !== 'slot_picker'));
    try {
      const res = await axios.post(`${API_BASE}/api/inbound/chat/widget/${token}/book`, { session_id: sessionId, meeting_type_id: mtId, start_time: start });
      if (res.data?.ok) {
        if (res.data.reply_seq) lastSeqRef.current = Math.max(lastSeqRef.current, res.data.reply_seq);
        if (res.data.reply) setMessages((m) => [...m, { role: 'assistant', content: res.data.reply }]);
      } else if (res.data?.error === 'need_email') {
        setMessages((m) => [...m, { role: 'assistant', content: 'Please share your email first so I can send the calendar invite.' }]);
        setCards((cs) => [...cs, { type: 'capture_form', title: 'Your details', subtitle: 'We need your email to send the invite.', fields: ['name', 'email', 'phone'] }]);
      }
    } catch { /* ignore */ }
  }, [sessionId, token]);

  const confirmCallMe = useCallback(async () => {
    setConsentAsk(false);
    setCards((cs) => cs.filter((c) => c.type !== 'actions'));
    try {
      const res = await axios.post(`${API_BASE}/api/inbound/chat/widget/${token}/request-call`, { session_id: sessionId, consent: true });
      if (res.data?.ok) setMessages((m) => [...m, { role: 'assistant', content: res.data.message }]);
      else if (res.data?.error === 'need_phone') setMessages((m) => [...m, { role: 'assistant', content: 'Please share your phone number first so we can call you.' }]);
      else if (res.data?.error === 'need_contact') setMessages((m) => [...m, { role: 'assistant', content: 'Please share your details first so we can call you.' }]);
    } catch { /* ignore */ }
  }, [sessionId, token]);

  const submitCsat = useCallback(async (rating) => {
    setCards((cs) => cs.filter((c) => c.type !== 'csat'));
    setMessages((m) => [...m, { role: 'assistant', sender: 'system', content: 'Thanks for your feedback! 🙏' }]);
    try { await axios.post(`${API_BASE}/api/inbound/chat/widget/${token}/csat`, { session_id: sessionId, rating }); } catch { /* ignore */ }
  }, [sessionId, token]);

  useEffect(() => {
    axios.get(`${API_BASE}/api/inbound/chat/widget/${token}/config`)
      .then((res) => {
        setConfig(res.data);
        if (res.data?.default_country) {
          setCapForm((f) => (f.country ? f : { ...f, country: res.data.default_country }));
        }
        if (res.data?.greeting) {
          setMessages((m) => (m.length <= 1 ? [{ role: 'assistant', content: res.data.greeting }] : m));
        }
      })
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
      });
  }, [token]);

  // Poll for new AI / human-rep messages (real-time rep takeover).
  useEffect(() => {
    if (!sessionId) return undefined;
    let stop = false;
    const tick = async () => {
      try {
        const res = await axios.get(`${API_BASE}/api/inbound/chat/widget/${token}/poll`, {
          params: { session_id: sessionId, after_seq: lastSeqRef.current },
        });
        const incoming = (res.data?.messages || []).filter((m) => m.seq > lastSeqRef.current);
        if (incoming.length && !stop) {
          lastSeqRef.current = Math.max(...incoming.map((m) => m.seq));
          setMessages((m) => [...m, ...incoming.map((x) => ({ role: 'assistant', content: x.content, sender: x.sender }))]);
        }
      } catch { /* ignore transient poll errors */ }
    };
    const id = setInterval(tick, 5000);
    return () => { stop = true; clearInterval(id); };
  }, [sessionId, token]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, sending]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || sending) return;
    setInput('');
    setMessages((m) => [...m, { role: 'user', content: text }]);
    setSending(true);
    try {
      const res = await axios.post(`${API_BASE}/api/inbound/chat/widget/${token}/message`, {
        session_id: sessionId,
        message: text,
      });
      const { session_id, reply, reply_seq, rep_takeover } = res.data || {};
      if (session_id && session_id !== sessionId) {
        setSessionId(session_id);
        localStorage.setItem(`lk_chat_${token}`, session_id);
      }
      if (reply_seq) lastSeqRef.current = Math.max(lastSeqRef.current, reply_seq);
      if (reply) {
        setMessages((m) => [...m, { role: 'assistant', content: reply }]);
      } else if (rep_takeover) {
        setMessages((m) => [...m, { role: 'assistant', sender: 'system', content: 'Sent — a specialist is here and will reply shortly.' }]);
      }
      if (res.data?.cards) setCards(res.data.cards);
    } catch {
      setMessages((m) => [...m, { role: 'assistant', content: 'Sorry, something went wrong. Please try again.' }]);
    } finally {
      setSending(false);
    }
  }, [input, sending, sessionId, token]);

  if (notFound) {
    return (
      <div data-testid="chat-widget-notfound" style={styles.center}>
        <MessageCircle size={28} style={{ opacity: 0.4, marginBottom: 8 }} />
        <p style={{ color: '#94a3b8', fontSize: 14 }}>This chat is unavailable.</p>
      </div>
    );
  }

  return (
    <div data-testid="chat-widget" style={styles.shell}>
      <div style={{ ...styles.header, background: color }}>
        <div style={styles.avatar}><MessageCircle size={18} /></div>
        <div>
          <div style={styles.title}>{config?.company_name || 'Chat'}</div>
          <div style={styles.status}><span style={styles.dot} /> We typically reply in a few minutes</div>
        </div>
        <button data-testid="chat-widget-close" onClick={closeWidget} title="Close" style={styles.closeBtn}>
          <X size={18} />
        </button>
      </div>

      <div ref={scrollRef} style={styles.body} data-testid="chat-widget-messages">
        {messages.map((m, i) => (
          <div key={i} style={{ ...styles.row, justifyContent: m.role === 'user' ? 'flex-end' : 'flex-start' }}>
            <div
              data-testid={`chat-msg-${m.role}`}
              style={{
                ...styles.bubble,
                background: m.role === 'user' ? color : '#1e293b',
                color: m.role === 'user' ? '#fff' : '#e2e8f0',
                borderBottomRightRadius: m.role === 'user' ? 4 : 14,
                borderBottomLeftRadius: m.role === 'user' ? 14 : 4,
                whiteSpace: 'pre-wrap',
              }}
            >
              {m.content}
            </div>
          </div>
        ))}
        {sending && (
          <div style={{ ...styles.row, justifyContent: 'flex-start' }}>
            <div style={{ ...styles.bubble, background: '#1e293b', color: '#94a3b8' }}>
              <Loader2 size={14} className="animate-spin" style={{ display: 'inline' }} /> typing…
            </div>
          </div>
        )}

        {cards.map((card, ci) => (
          <div key={`card-${ci}`} style={styles.card} data-testid={`chat-card-${card.type}`}>
            {card.type === 'capture_form' && (
              <div>
                <div style={styles.cardTitle}>{card.title}</div>
                {card.subtitle && <div style={styles.cardSub}>{card.subtitle}</div>}
                <input data-testid="capture-name" placeholder="Name" value={capForm.name}
                  onChange={(e) => setCapForm((f) => ({ ...f, name: e.target.value }))} style={styles.cardInput} />
                <input data-testid="capture-email" placeholder="Work email" value={capForm.email}
                  onChange={(e) => setCapForm((f) => ({ ...f, email: e.target.value }))} style={styles.cardInput} />
                <div style={{ display: 'flex', gap: 6 }}>
                  <select data-testid="capture-country" value={capForm.country}
                    onChange={(e) => setCapForm((f) => ({ ...f, country: e.target.value }))}
                    style={{ ...styles.cardInput, flex: '0 0 40%', marginBottom: 0 }} aria-label="Country code">
                    {DIAL_CODES.map((c) => (
                      <option key={c.iso} value={c.iso}>{c.flag} {c.iso} {c.dial}</option>
                    ))}
                  </select>
                  <input data-testid="capture-phone" placeholder="Phone (optional)" value={capForm.phone}
                    onChange={(e) => setCapForm((f) => ({ ...f, phone: e.target.value }))}
                    style={{ ...styles.cardInput, flex: 1, marginBottom: 0 }} />
                </div>
                <button data-testid="capture-submit" onClick={submitCapture} disabled={!capForm.email && !capForm.phone}
                  style={{ ...styles.cardBtn, background: color, opacity: (!capForm.email && !capForm.phone) ? 0.5 : 1 }}>Submit</button>
              </div>
            )}
            {card.type === 'actions' && (
              <div style={styles.actionRow}>
                <button data-testid="action-book-call" onClick={doBookCall} style={{ ...styles.chip, borderColor: color }}>
                  <Calendar size={14} /> Book a call
                </button>
                <button data-testid="action-call-me" onClick={() => setConsentAsk(true)} style={{ ...styles.chip, borderColor: color }}>
                  <Phone size={14} /> Call me now
                </button>
              </div>
            )}
            {card.type === 'csat' && (
              <div>
                <div style={styles.cardTitle}>{card.title}</div>
                <div style={styles.starRow}>
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button key={n} data-testid={`csat-star-${n}`} onClick={() => submitCsat(n)} style={styles.star}><Star size={22} /></button>
                  ))}
                </div>
              </div>
            )}
            {card.type === 'slot_picker' && (() => {
              const mtId = slotMt || card.meeting_type;
              const day = card.days[slotDay] || card.days[0];
              return (
                <div>
                  <div style={styles.cardTitle}><Calendar size={14} style={{ display: 'inline', marginRight: 4 }} /> Pick a time</div>
                  {card.meeting_types.length > 1 && (
                    <div style={{ ...styles.actionRow, marginBottom: 8 }}>
                      {card.meeting_types.map((mt) => (
                        <button key={mt.id} data-testid={`slot-mt-${mt.id}`} onClick={() => changeMt(mt.id)}
                          style={{ ...styles.chip, borderColor: mtId === mt.id ? color : '#334155', color: mtId === mt.id ? '#fff' : '#e2e8f0' }}>{mt.name}</button>
                      ))}
                    </div>
                  )}
                  <div style={styles.actionRow}>
                    {card.days.map((d, di) => (
                      <button key={d.date} data-testid={`slot-day-${di}`} onClick={() => setSlotDay(di)}
                        style={{ ...styles.chip, borderColor: slotDay === di ? color : '#334155' }}>{d.label}</button>
                    ))}
                  </div>
                  <div style={{ ...styles.actionRow, marginTop: 8 }}>
                    {(day?.slots || []).map((s) => (
                      <button key={s.start} data-testid="slot-time" onClick={() => bookSlot(mtId, s.start)}
                        style={{ ...styles.chip, borderColor: color }}>{s.display}</button>
                    ))}
                    {!(day?.slots || []).length && <div style={styles.cardSub}>No open times that day — try another.</div>}
                  </div>
                </div>
              );
            })()}
          </div>
        ))}

        {consentAsk && (
          <div style={styles.card} data-testid="call-consent">
            <div style={styles.cardSub}>We’ll call the number you shared. OK to proceed?</div>
            <div style={styles.actionRow}>
              <button data-testid="consent-yes" onClick={confirmCallMe} style={{ ...styles.cardBtn, background: color, width: 'auto', padding: '8px 16px' }}>Yes, call me</button>
              <button data-testid="consent-no" onClick={() => setConsentAsk(false)} style={styles.chip}>Cancel</button>
            </div>
          </div>
        )}
      </div>

      <div style={styles.inputBar}>
        <input
          data-testid="chat-widget-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(); } }}
          placeholder="Type your message…"
          style={styles.input}
        />
        <button data-testid="chat-widget-send" onClick={send} disabled={sending || !input.trim()}
          style={{ ...styles.sendBtn, background: color, opacity: sending || !input.trim() ? 0.5 : 1 }}>
          <Send size={16} />
        </button>
      </div>
    </div>
  );
}

const styles = {
  shell: { display: 'flex', flexDirection: 'column', height: '100vh', maxHeight: '100vh',
    background: '#0a0f1a', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' },
  center: { height: '100vh', display: 'flex', flexDirection: 'column', alignItems: 'center',
    justifyContent: 'center', background: '#0a0f1a' },
  header: { display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', color: '#fff' },
  closeBtn: { marginLeft: 'auto', background: 'rgba(255,255,255,0.18)', border: 'none', color: '#fff',
    width: 30, height: 30, borderRadius: 8, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' },
  card: { background: '#111a2e', border: '1px solid #24314d', borderRadius: 12, padding: 12, marginTop: 4 },
  cardTitle: { color: '#e2e8f0', fontSize: 13.5, fontWeight: 700, marginBottom: 2 },
  cardSub: { color: '#94a3b8', fontSize: 12, marginBottom: 8 },
  cardInput: { width: '100%', boxSizing: 'border-box', background: '#1e293b', border: '1px solid #334155',
    borderRadius: 8, padding: '8px 11px', color: '#e2e8f0', fontSize: 13, outline: 'none', marginBottom: 7 },
  cardBtn: { border: 'none', borderRadius: 8, width: '100%', color: '#fff', cursor: 'pointer',
    padding: '9px 0', fontSize: 13, fontWeight: 600 },
  actionRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 6, background: 'transparent',
    border: '1px solid #334155', color: '#e2e8f0', borderRadius: 20, padding: '7px 13px', fontSize: 12.5, cursor: 'pointer' },
  starRow: { display: 'flex', gap: 6 },
  star: { background: 'transparent', border: 'none', color: '#fbbf24', cursor: 'pointer', padding: 2 },
  avatar: { width: 34, height: 34, borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
    display: 'flex', alignItems: 'center', justifyContent: 'center' },
  title: { fontSize: 15, fontWeight: 700 },
  status: { fontSize: 11, opacity: 0.9, display: 'flex', alignItems: 'center', gap: 5, marginTop: 2 },
  dot: { width: 7, height: 7, borderRadius: '50%', backgroundColor: '#4ade80' },
  body: { flex: 1, overflowY: 'auto', padding: 16, display: 'flex', flexDirection: 'column', gap: 10 },
  row: { display: 'flex', width: '100%' },
  bubble: { maxWidth: '80%', padding: '9px 13px', borderRadius: 14, fontSize: 13.5, lineHeight: 1.45 },
  inputBar: { display: 'flex', gap: 8, padding: 12, borderTop: '1px solid #1e293b', background: '#0f172a' },
  input: { flex: 1, background: '#1e293b', border: '1px solid #334155', borderRadius: 10,
    padding: '10px 14px', color: '#e2e8f0', fontSize: 14, outline: 'none' },
  sendBtn: { border: 'none', borderRadius: 10, width: 42, color: '#fff', cursor: 'pointer',
    display: 'flex', alignItems: 'center', justifyContent: 'center' },
};
